<template>
  <a-modal
    :open="visible"
    title="QR 태그 관리"
    :width="600"
    :footer="null"
    @cancel="handleCancel"
  >
    <div class="modal-content">
      <!-- 태그 추가 폼 -->
      <div class="add-form">
        <a-input
          v-model:value="newTag.name"
          placeholder="태그명을 입력하세요"
          style="flex: 1"
          @pressEnter="addTag"
        />
        <div class="color-picker-wrapper">
          <label>색상:</label>
          <input
            type="color"
            v-model="newTag.color"
            class="color-input"
          />
        </div>
        <a-button type="primary" class="yellow-button" @click="addTag" :loading="adding">
          추가
        </a-button>
      </div>

      <!-- 태그 목록 -->
      <div class="tag-list">
        <a-table
          :columns="columns"
          :data-source="tags"
          :pagination="false"
          size="small"
          :row-key="record => record._id"
        >
          <template #name="{ record }">
            <div v-if="editingId === record._id" class="edit-cell">
              <a-input
                v-model:value="editData.name"
                size="small"
                @pressEnter="saveEdit(record)"
              />
            </div>
            <span
              v-else
              class="tag-badge"
              :style="{ backgroundColor: (record.color || '#007bff') + '20', color: record.color || '#007bff' }"
            >
              {{ record.name }}
            </span>
          </template>

          <template #color="{ record }">
            <div v-if="editingId === record._id" class="edit-cell">
              <input
                type="color"
                v-model="editData.color"
                class="color-input small"
              />
            </div>
            <div v-else class="color-preview" :style="{ backgroundColor: record.color || '#007bff' }"></div>
          </template>

          <template #qrCount="{ record }">
            <span>{{ record.qrCount || 0 }}</span>
          </template>

          <template #action="{ record }">
            <div class="action-buttons" v-if="editingId === record._id">
              <a-button type="link" size="small" @click="saveEdit(record)" :loading="saving">
                저장
              </a-button>
              <a-button type="link" size="small" @click="cancelEdit">
                취소
              </a-button>
            </div>
            <div class="action-buttons" v-else>
              <a-button type="link" size="small" @click="startEdit(record)">
                수정
              </a-button>
              <a-popconfirm
                title="이 태그를 삭제하시겠습니까?"
                ok-text="삭제"
                cancel-text="취소"
                @confirm="deleteTag(record)"
              >
                <a-button type="link" size="small" danger>
                  삭제
                </a-button>
              </a-popconfirm>
            </div>
          </template>
        </a-table>

        <a-empty v-if="tags.length === 0" description="등록된 태그가 없습니다" />
      </div>
    </div>
  </a-modal>
</template>

<script>
import { defineComponent, ref, watch } from 'vue';
import { message } from 'ant-design-vue';
import QrService from '../service/QrService';

export default defineComponent({
  name: 'TagManageModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    tags: {
      type: Array,
      default: () => []
    }
  },
  emits: ['update:visible', 'refresh'],
  setup(props, { emit }) {
    const adding = ref(false);
    const saving = ref(false);
    const editingId = ref(null);
    const newTag = ref({ name: '', color: '#007bff' });
    const editData = ref({ name: '', color: '#007bff' });

    const columns = [
      {
        title: '태그명',
        key: 'name',
        slots: { customRender: 'name' }
      },
      {
        title: '색상',
        key: 'color',
        width: 80,
        align: 'center',
        slots: { customRender: 'color' }
      },
      {
        title: 'QR수',
        key: 'qrCount',
        width: 80,
        align: 'center',
        slots: { customRender: 'qrCount' }
      },
      {
        title: '',
        key: 'action',
        width: 120,
        align: 'center',
        slots: { customRender: 'action' }
      }
    ];

    watch(() => props.visible, (newVal) => {
      if (!newVal) {
        editingId.value = null;
        newTag.value = { name: '', color: '#007bff' };
      }
    });

    const handleCancel = () => {
      emit('update:visible', false);
    };

    const addTag = async () => {
      if (!newTag.value.name?.trim()) {
        message.warning('태그명을 입력해주세요.');
        return;
      }

      adding.value = true;
      try {
        const success = await QrService.createTag({
          name: newTag.value.name.trim(),
          color: newTag.value.color
        });

        if (success) {
          newTag.value = { name: '', color: '#007bff' };
          emit('refresh');
        }
      } finally {
        adding.value = false;
      }
    };

    const startEdit = (record) => {
      editingId.value = record._id;
      editData.value = {
        name: record.name,
        color: record.color || '#007bff'
      };
    };

    const cancelEdit = () => {
      editingId.value = null;
      editData.value = { name: '', color: '#007bff' };
    };

    const saveEdit = async (record) => {
      if (!editData.value.name?.trim()) {
        message.warning('태그명을 입력해주세요.');
        return;
      }

      saving.value = true;
      try {
        const success = await QrService.updateTag(record._id, {
          name: editData.value.name.trim(),
          color: editData.value.color
        });

        if (success) {
          editingId.value = null;
          emit('refresh');
        }
      } finally {
        saving.value = false;
      }
    };

    const deleteTag = async (record) => {
      const success = await QrService.deleteTag(record._id);
      if (success) {
        emit('refresh');
      }
    };

    return {
      adding,
      saving,
      editingId,
      newTag,
      editData,
      columns,
      handleCancel,
      addTag,
      startEdit,
      cancelEdit,
      saveEdit,
      deleteTag
    };
  }
});
</script>

<style lang="scss" scoped>
@import '../styles/variables.scss';

.modal-content {
  max-height: 60vh;
  overflow-y: auto;
}

.add-form {
  display: flex;
  align-items: center;
  gap: $spacing-sm;
  margin-bottom: $spacing-md;
  padding-bottom: $spacing-md;
  border-bottom: 1px solid $border-color;

  .color-picker-wrapper {
    display: flex;
    align-items: center;
    gap: $spacing-xs;

    label {
      font-size: $font-size-sm;
      color: $text-muted;
    }
  }
}

.color-input {
  width: 40px;
  height: 32px;
  border: 1px solid $border-color;
  border-radius: $border-radius-sm;
  cursor: pointer;
  padding: 0;

  &.small {
    width: 32px;
    height: 24px;
  }
}

.tag-list {
  .edit-cell {
    padding-right: $spacing-xs;
  }

  .tag-badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: $font-size-xs;
    font-weight: $font-weight-medium;
  }

  .color-preview {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    margin: 0 auto;
  }

  .action-buttons {
    display: flex;
    gap: $spacing-xs;
    justify-content: center;
  }
}

.yellow-button {
  background-color: $yellow-button-color;
  border-color: $yellow-button-color;
  color: $text-dark;

  &:hover {
    background-color: $yellow-button-color-hover;
    border-color: $yellow-button-color-hover;
  }
}
</style>
